// frontend/src/App.jsx
// ------------------------------------------------------------
// Main app routing for KirunaTours.com
// - Public routes
// - Travel Guide routes (replaces old blog entries)
// - Tour category routes before /tours/:slug
// - Booking iframe route without MainLayout
// - Protected admin routes with token validation
// ------------------------------------------------------------

import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import ScrollToTop from "./components/ScrollToTop";
import Footer from "./components/Footer";
import AIButton from "./components/AIButton";
import { AIProvider } from "./context/AIContext";

// Home is intentionally eager-loaded because it is the initial page
// for the root route and should render as quickly as possible.
import Home from "./pages/Home";

// All other public pages are code-split so they are not included
// in the JavaScript bundle required for the homepage.
const About = lazy(() => import("./pages/About"));
const Tours = lazy(() => import("./pages/Tours"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TravelGuideHub = lazy(() => import("./pages/TravelGuideHub"));
const TravelGuideArticle = lazy(() => import("./pages/TravelGuideArticle"));
const TravelGuideCategoryHub = lazy(() =>
  import("./pages/TravelGuideCategoryHub")
);

// Other lazy-loaded pages
const TourDetails = lazy(() => import("./pages/TourDetails"));
const BookTour = lazy(() => import("./pages/BookTour"));
const StripeCheckout = lazy(() => import("./pages/StripeCheckout"));
const Success = lazy(() => import("./pages/Success"));
const Cancel = lazy(() => import("./pages/Cancel"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminTravelGuides = lazy(() => import("./pages/AdminTravelGuides"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const AdminTourDates = lazy(() => import("./pages/AdminTourDates"));
const AdminChatLogs = lazy(() => import("./pages/AdminChatLogs"));

function LoadingFallback() {
  return (
    <div className="route-loading-fallback">
      Loading...
    </div>
  );
}

function ProtectedAdmin() {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("kt_admin_token") : null;

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      try {
        const res = await fetch("/api/admin/validate-token", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
        });

        if (!res.ok) {
          localStorage.removeItem("kt_admin_token");
          localStorage.removeItem("kt_admin_email");
          setIsValid(false);
          return;
        }

        const data = await res.json().catch(() => ({}));
        setIsValid(!!data?.valid);
      } catch {
        localStorage.removeItem("kt_admin_token");
        localStorage.removeItem("kt_admin_email");
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  if (isValidating) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Verifierar inloggning…</div>;
  }

  if (!isValid) {
    return <Navigate to="/secure-admin-42/login" replace />;
  }

  return <Outlet />;
}

function MainLayout({ children, showAI }) {
  const location = useLocation();

  // Keep the footer below the viewport while an individual tour route
  // is waiting for its lazy-loaded React chunk and/or API data.
  // This prevents the large footer layout shift measured by Lighthouse.
  const isTourDetailRoute = /^\/tours\/[^/]+\/?$/.test(location.pathname);

  const isTravelGuideSingleRoute =
    /^\/travel-guide\/[^/]+\/?$/.test(location.pathname);

  const reserveContentHeight =
    isTourDetailRoute || isTravelGuideSingleRoute;

  const handleLogout = () => {
    localStorage.removeItem("kt_admin_token");
    localStorage.removeItem("kt_admin_email");
    window.location.href = "/secure-admin-42/login";
  };

  return (
    <>
      <header style={{ position: "relative", height: "300px", overflow: "hidden" }}>
        <img
          src="/header.webp"
          alt="Kiruna Tours Arctic Header"
          fetchPriority="high"
          loading="eager"
          width="1920"
          height="300"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        />
      </header>

      <nav
        style={{
          display: "flex",
          gap: "1rem",
          justifyContent: "center",
          padding: "1rem",
          background: "#003366",
          color: "white",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Link to="/" style={{ color: "white", textDecoration: "none" }}>
          Home
        </Link>

        <Link to="/tours/" style={{ color: "white", textDecoration: "none" }}>
          Tours
        </Link>

        <Link to="/travel-guide/" style={{ color: "white", textDecoration: "none" }}>
          Travel Guide
        </Link>

        <Link to="/about/" style={{ color: "white", textDecoration: "none" }}>
          About
        </Link>

        <Link to="/contact/" style={{ color: "white", textDecoration: "none" }}>
          Contact
        </Link>

        {location.pathname.startsWith("/secure-admin-42") && (
          <button
            onClick={handleLogout}
            style={{
              background: "transparent",
              border: "1px solid white",
              color: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              cursor: "pointer",
              marginLeft: "2rem",
              fontSize: "14px",
            }}
          >
            Logga ut
          </button>
        )}
      </nav>

      <div
        style={
          reserveContentHeight
            ? {
                minHeight: "100vh",
                width: "100%",
              }
            : undefined
        }
      >
        <Suspense fallback={<LoadingFallback />}>
          {children}
        </Suspense>
      </div>

      <Footer />

      {showAI && (
        <div style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 999 }}>
          <AIButton />
        </div>
      )}
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isIframeRoute = location.pathname.startsWith("/book/");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("notfound")) {
      console.log("404 for path:", params.get("notfound"));
    }
  }, [location]);

  return (
    <Routes>
      {/* Booking iframe route without MainLayout */}
      <Route
        path="/book/:id"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <BookTour />
          </Suspense>
        }
      />

      {!isIframeRoute && (
        <>
          {/* Travel Guide routes */}
          <Route
            path="/travel-guide"
            element={
              <MainLayout showAI={false}>
                <TravelGuideHub />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideHub />
              </MainLayout>
            }
          />
          {/* Travel Guide thematic hubs */}
          <Route
            path="/travel-guide/northern-lights"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="northern-lights" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/northern-lights/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="northern-lights" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/winter-travel"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="winter-travel" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/winter-travel/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="winter-travel" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/abisko"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="abisko" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/abisko/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="abisko" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/activities"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="activities" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/activities/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="activities" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/accommodation-food"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="accommodation-food" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/accommodation-food/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="accommodation-food" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/trip-planning"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="trip-planning" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/trip-planning/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="trip-planning" />
              </MainLayout>
            }
          />

          <Route
            path="/travel-guide/easy-arctic-travel"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="easy-arctic-travel" />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/easy-arctic-travel/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideCategoryHub categorySlug="easy-arctic-travel" />
              </MainLayout>
            }
          />


          <Route
            path="/travel-guide/:slug"
            element={
              <MainLayout showAI={false}>
                <TravelGuideArticle />
              </MainLayout>
            }
          />
          <Route
            path="/travel-guide/:slug/"
            element={
              <MainLayout showAI={false}>
                <TravelGuideArticle />
              </MainLayout>
            }
          />

          {/* Public pages */}
          <Route
            path="/"
            element={
              <MainLayout showAI={true}>
                <Home />
              </MainLayout>
            }
          />

          <Route
            path="/about"
            element={
              <MainLayout showAI={false}>
                <About />
              </MainLayout>
            }
          />
          <Route
            path="/about/"
            element={
              <MainLayout showAI={false}>
                <About />
              </MainLayout>
            }
          />

          {/* Tours landing */}
          <Route
            path="/tours"
            element={
              <MainLayout showAI={false}>
                <Tours />
              </MainLayout>
            }
          />
          <Route
            path="/tours/"
            element={
              <MainLayout showAI={false}>
                <Tours />
              </MainLayout>
            }
          />

          {/* Tour category pages before /tours/:slug */}
          <Route
            path="/tours/abisko"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="abisko" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/abisko/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="abisko" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/northern-lights"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="northern-lights" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/northern-lights/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="northern-lights" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/dog-sledding"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="dog-sledding" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/dog-sledding/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="dog-sledding" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/snowmobile"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="snowmobile" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/snowmobile/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="snowmobile" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/reindeer"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="reindeer" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/reindeer/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="reindeer" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/snowshoe"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="snowshoe" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/snowshoe/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="snowshoe" />
              </MainLayout>
            }
          />

          <Route
            path="/tours/arctic-dining"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="arctic-dining" />
              </MainLayout>
            }
          />
          <Route
            path="/tours/arctic-dining/"
            element={
              <MainLayout showAI={false}>
                <Tours initialCategory="arctic-dining" />
              </MainLayout>
            }
          />

          {/* Single tour */}
          <Route
            path="/tours/:slug"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <TourDetails />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/tours/:slug/"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <TourDetails />
                </Suspense>
              </MainLayout>
            }
          />

          <Route
            path="/faq"
            element={
              <MainLayout showAI={false}>
                <FAQ />
              </MainLayout>
            }
          />
          <Route
            path="/faq/"
            element={
              <MainLayout showAI={false}>
                <FAQ />
              </MainLayout>
            }
          />

          {/* Secure Fallbacks: Redirect old blog URLs directly to the new travel guide */}
          <Route path="/blog" element={<Navigate to="/travel-guide/" replace />} />
          <Route path="/blog/" element={<Navigate to="/travel-guide/" replace />} />
          <Route path="/blog/:slug" element={<Navigate to="/travel-guide/" replace />} />
          <Route path="/blog/:slug/" element={<Navigate to="/travel-guide/" replace />} />

          <Route
            path="/contact"
            element={
              <MainLayout showAI={false}>
                <Contact />
              </MainLayout>
            }
          />
          <Route
            path="/contact/"
            element={
              <MainLayout showAI={false}>
                <Contact />
              </MainLayout>
            }
          />

          <Route
            path="/privacy"
            element={
              <MainLayout showAI={false}>
                <Privacy />
              </MainLayout>
            }
          />
          <Route
            path="/privacy/"
            element={
              <MainLayout showAI={false}>
                <Privacy />
              </MainLayout>
            }
          />

          <Route
            path="/checkout"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <StripeCheckout />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/checkout/"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <StripeCheckout />
                </Suspense>
              </MainLayout>
            }
          />

          <Route
            path="/success"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <Success />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/success/"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <Success />
                </Suspense>
              </MainLayout>
            }
          />

          <Route
            path="/cancel"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <Cancel />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/cancel/"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <Cancel />
                </Suspense>
              </MainLayout>
            }
          />

          {/* Open admin login route */}
          <Route
            path="/secure-admin-42/login"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLogin />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/secure-admin-42/login/"
            element={
              <MainLayout showAI={false}>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLogin />
                </Suspense>
              </MainLayout>
            }
          />

          {/* Old login/admin aliases */}
          <Route path="/login" element={<Navigate to="/secure-admin-42/login" replace />} />
          <Route path="/login/" element={<Navigate to="/secure-admin-42/login" replace />} />

          <Route path="/admin" element={<Navigate to="/secure-admin-42/login" replace />} />
          <Route path="/admin/" element={<Navigate to="/secure-admin-42/login" replace />} />
          <Route path="/admin/dashboard" element={<Navigate to="/secure-admin-42/dashboard" replace />} />
          <Route path="/admin/dashboard/" element={<Navigate to="/secure-admin-42/dashboard" replace />} />
          <Route path="/admin/bookings" element={<Navigate to="/secure-admin-42/bookings" replace />} />
          <Route path="/admin/bookings/" element={<Navigate to="/secure-admin-42/bookings" replace />} />
          <Route path="/admin/tour-dates" element={<Navigate to="/secure-admin-42/tour-dates" replace />} />
          <Route path="/admin/tour-dates/" element={<Navigate to="/secure-admin-42/tour-dates" replace />} />
          <Route path="/admin/chat-logs" element={<Navigate to="/secure-admin-42/chat-logs" replace />} />
          <Route path="/admin/chat-logs/" element={<Navigate to="/secure-admin-42/chat-logs" replace />} />

          <Route path="/admin/travel-guides" element={<Navigate to="/secure-admin-42/travel-guides" replace />} />
          <Route path="/admin/travel-guides/" element={<Navigate to="/secure-admin-42/travel-guides" replace />} />

          {/* Protected admin pages */}
          <Route element={<ProtectedAdmin />}>
            <Route
              path="/secure-admin-42"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/dashboard"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/dashboard/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </MainLayout>
              }
            />

            <Route
              path="/secure-admin-42/bookings"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminBookings />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/bookings/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminBookings />
                  </Suspense>
                </MainLayout>
              }
            />

            <Route
              path="/secure-admin-42/tour-dates"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTourDates />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/tour-dates/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTourDates />
                  </Suspense>
                </MainLayout>
              }
            />

            <Route
              path="/secure-admin-42/travel-guides"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTravelGuides />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/travel-guides/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTravelGuides />
                  </Suspense>
                </MainLayout>
              }
            />

            <Route
              path="/secure-admin-42/chat-logs"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminChatLogs />
                  </Suspense>
                </MainLayout>
              }
            />
            <Route
              path="/secure-admin-42/chat-logs/"
              element={
                <MainLayout showAI={false}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminChatLogs />
                  </Suspense>
                </MainLayout>
              }
            />
          </Route>

          {/* 404 must be last */}
          <Route
            path="*"
            element={
              <MainLayout showAI={false}>
                <NotFound />
              </MainLayout>
            }
          />
        </>
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AIProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AIProvider>
    </HelmetProvider>
  );
}